import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getWeddings } from '../../firebase/firestore';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { Add as AddIcon, Event as EventIcon } from '@mui/icons-material';

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData, isAdmin } = useAuth();
  
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchWeddings = async () => {
      try {
        if (!userData) return;
        
        const weddingsData = await getWeddings(userData.uid, isAdmin);
        setWeddings(weddingsData);
      } catch (err) {
        console.error('Error fetching weddings:', err);
        setError(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchWeddings();
  }, [userData, isAdmin, t]);

  const handleCreateWedding = () => {
    navigate('/weddings/create');
  };

  const handleViewWedding = (weddingId) => {
    navigate(`/weddings/${weddingId}`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          {t('dashboard.welcome')}, {userData?.name || ''}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateWedding}
        >
          {t('dashboard.createWedding')}
        </Button>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Typography variant="h5" sx={{ mb: 3 }}>
        {t('dashboard.yourWeddings')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : weddings.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('dashboard.noWeddings')}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {weddings.map((wedding) => (
            <Grid item xs={12} sm={6} md={4} key={wedding.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="div">
                      {wedding.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('weddings.location')}: {wedding.location}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('weddings.date')}: {wedding.date ? new Date(wedding.date.toDate()).toLocaleDateString() : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {t('weddings.tables')}: {wedding.tables ? wedding.tables.length : 0}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    onClick={() => handleViewWedding(wedding.id)}
                    sx={{ ml: 'auto' }}
                  >
                    {t('common.view')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default Dashboard;
